import { getIncompleteWorkflowStepTemplate } from './stepTemplates';
import branchHasAnyExecution from './branchHasAnyExecution';
import buildTriggerStep from './buildTriggerStep';

const getLogTimeMachine = ({ output, operationLogs, time, search, triggerStep, invokedBy, manualUser, manualUserId, incomplete }) => {
  let allSteps = [];
  const timeMachine = [];
  const metaSteps = output?.meta?.steps;
  const numericTime = typeof time === 'string' ? parseInt(time, 10) : time;

  const triggerResult = buildTriggerStep({ timeMachine, triggerStep, output, invokedBy, manualUser, manualUserId });
  if (triggerResult) {
    allSteps.push(triggerResult.stepEntry);
    timeMachine.push(triggerResult.timeMachineEntry);
  }

  const iterateSiblings = (set = [], levels = [0], indent = 0) => {
    for (let i = 0; i < set.length; ++i) {
      const step = set[i];
      const metaStep = metaSteps?.find((n) => n?.id === step?.stepId);

      timeMachine.push({ index: timeMachine.length, stepId: step?.stepId, step, siblings: set?.length, children: step?.children?.length });
      const stepIndex = timeMachine.length - 1;
      const loopTimes = [stepIndex];
      const isIfElseIndex = step?.children?.findIndex((o) => o?.[0]?.ran === false);
      const isIfElse = isIfElseIndex > -1;
      const isError = step?.response?.success === false;

      if (!allSteps?.find((n) => n?.stepId === step?.stepId)) {
        allSteps.push({ stepId: step?.stepId, indent, stepNumber: metaStep?.stepNumber });
      }

      const allStepsStep = allSteps.find((n) => n?.stepId === step?.stepId);

      if (allStepsStep.firstTime === undefined) {
        allStepsStep.firstTime = stepIndex;
      }

      // Capture response data for searching later
      if (step.response && step.response.data) {
        if (!allStepsStep.searchableData) {
          allStepsStep.searchableData = [];
        }

        const responseData = Array.isArray(step.response.data) ? step.response.data : [step.response.data];
        const newSearchableData =
          responseData
            ?.filter((dataEntry) => !!dataEntry && typeof dataEntry === 'object' && !Array.isArray(dataEntry))
            ?.map((dataEntry) => {
              const keys = Object.keys(dataEntry);
              // Skip objects with too many properties to prevent enumeration errors
              if (keys.length > 10000) {
                return { data: {}, time: stepIndex, message: metaStep?.message };
              }
              return keys.reduce(
                (acc, key) => {
                  acc.data[key] = dataEntry[key]; // Flattening key-value pairs for easier search
                  return acc;
                },
                { data: {}, time: stepIndex, message: metaStep?.message }
              );
            }) || [];

        allStepsStep.searchableData.push(...newSearchableData);
      }

      if (stepIndex <= numericTime) {
        allStepsStep.lastIndex = stepIndex;
        allStepsStep.lastOutput = step;
      }

      if (isError) {
        allStepsStep.errors = allStepsStep.errors || 0;
        allStepsStep.errors++;
      }

      if (step?.children?.length > 0) {
        for (let j = 0; j < step.children.length; ++j) {
          const childSet = step.children[j];
          const childEntryStep = childSet?.[0];

          if (isIfElse) {
            if (!allSteps?.find((n) => n?.stepId === childEntryStep?.stepId) && childEntryStep?.stepId) {
              const childMetaStep = metaSteps?.find((n) => n?.id === childEntryStep?.stepId);
              allSteps.push({ stepId: childEntryStep?.stepId, indent: indent + 1, stepNumber: childMetaStep?.stepNumber });
            }

            // Only drop a branch when nothing in it actually executed. A branch whose
            // first step is manually skipped but whose later steps did run must still
            // be rendered, otherwise the timeline silently truncates and downstream
            // errors / operation logs become unreachable from the UI.
            if (!branchHasAnyExecution(childSet)) {
              const childStep = allSteps.find((n) => n?.stepId === childEntryStep?.stepId);
              if (childStep && stepIndex < numericTime) {
                childStep.lastOutput = undefined;
              }
              continue;
            }
          }

          iterateSiblings(childSet, [...levels, j], indent + 1);
          if (j < step.children.length - 1) {
            loopTimes.push(parseFloat(timeMachine.length));
          }
        }
        if (!isIfElse) {
          timeMachine[stepIndex].loopTimes = loopTimes;
        }
      }

      if (!allStepsStep.errors) {
        allStepsStep.errors = operationLogs?.filter((n) => n?.stepId === step?._id && n?.actionStatus === 'error')?.length;
      }
    }
  };

  iterateSiblings(output?.data);

  allSteps = allSteps
    .map((n, idx) => ({
      ...n,
      stepNumber: n.trigger === true ? undefined : n.stepNumber || idx + 1
    }))
    .sort((a, b) => a.stepNumber - b.stepNumber);

  if (incomplete) {
    // Add a placeholder step at the end if the log is incomplete
    const { stepEntry, timeMachineEntry } = getIncompleteWorkflowStepTemplate({ allSteps, timeMachine });
    allSteps.push(stepEntry);
    timeMachine.unshift(timeMachineEntry);
  }

  let searchTime = 0;

  // Search function
  const searchInSteps = (query) => {
    if (!query) return allSteps;

    const normalizedQuery = query.toLowerCase().trim();

    return allSteps.filter((step) => {
      const searchableStrings =
        step.searchableData?.map((data) =>
          JSON.stringify(data)
            ?.replace(/"([^"]+)":/g, '$1: ')
            .replace(/"/g, '')
        ) || [];

      const matchIndex = searchableStrings.findIndex((str) => str?.toLowerCase().includes(normalizedQuery));

      if (matchIndex !== -1 && !searchTime) {
        searchTime = step.searchableData[matchIndex].time;
      }

      return matchIndex !== -1;
    });
  };

  // If a search query is provided, filter the results based on that
  const searchedSteps = search ? searchInSteps(search) : allSteps;

  return { allSteps: searchedSteps, timeMachine, searchTime };
};

export default getLogTimeMachine;
